import { SUPPORT_CATEGORIES, type SupportCategory } from '@iqrapath/shared'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, Mail } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { EmptyState } from '../../components/EmptyState.tsx'
import { SkCard } from '../../components/Skeleton.tsx'
import { ApiError, api } from '../../lib/api.ts'
import { useSession } from '../../lib/queries.ts'

type Info = { ok: true; enabled: boolean; email: string | null }
const MAX = 4000

/** Short answers for what people ask most; true for this app as built. */
const FAQ: [string, string][] = [
  ['Lupa kode lisensi?', 'Kode dikirim ke email yang kamu pakai saat membeli. Cari email dari IqraPath di kotak masuk atau folder spam.'],
  ['Pindah atau ganti HP?', 'Masuk di HP baru dengan email dan kode lisensi yang sama. Progresmu tersimpan di akun, bukan di HP.'],
  ['Pengingat tidak muncul?', 'Pastikan pengingat aktif dan browser diizinkan mengirim notifikasi (Pengaturan → Pengingat harian). Di iPhone, buka IqraPath dari layar utama.'],
]

/** /bantuan: in-app support form → CloudPass inbox (docs/PRD.md M12, docs/cloudpass-support-app-integration.md). */
export function BantuanPage() {
  const { data: session } = useSession()
  const info = useQuery({ queryKey: ['bantuan'], queryFn: () => api<Info>('GET', '/api/bantuan'), staleTime: 5 * 60_000 })
  const [category, setCategory] = useState<SupportCategory>('teknis')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [ticket, setTicket] = useState<number | null>(null)
  const email = session?.state === 'ok' ? session.me.user.email : ''

  const send = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setSending(true)
    try {
      const client = { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, viewport: `${window.innerWidth}x${window.innerHeight}` }
      const r = await api<{ ok: true; ticket: number }>('POST', '/api/bantuan', { category, subject, message, client })
      setTicket(r.ticket)
      setSubject('')
      setMessage('')
    } catch (err) {
      const extra = info.data?.email ? ` Kamu juga bisa email langsung ke ${info.data.email}.` : ''
      setError(err instanceof ApiError ? `${err.message}${err.code === 'support_failed' ? extra : ''}` : 'Belum terkirim. Coba lagi.')
    } finally {
      setSending(false)
    }
  }

  const ready = subject.trim().length >= 3 && message.trim().length >= 5
  return (
    <div className="page bantuan">
      <Link className="back" to="/pengaturan"><ChevronLeft size={18} aria-hidden="true" /> Pengaturan</Link>
      <h1>Bantuan</h1>

      <section aria-labelledby="faq-h">
        <h2 className="section-t" id="faq-h">Pertanyaan yang sering muncul</h2>
        <div className="card faq">
          {FAQ.map(([q, a]) => (
            <details key={q}>
              <summary>{q}</summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
      </section>

      <h2 className="section-t">Kirim pesan ke tim</h2>
      {info.isPending && <SkCard label="Memuat" lines={4} h={200} />}
      {info.isError && (
        <EmptyState obj="jam-pasir" title="Belum bisa dimuat" action={<button className="btn btn-secondary" onClick={() => info.refetch()}>Coba lagi</button>}>
          Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.
        </EmptyState>
      )}
      {info.data && !info.data.enabled && (
        <div className="card set-row">
          <span className="ic" aria-hidden="true"><Mail size={20} /></span>
          {info.data.email ? (
            <p style={{ flex: 1 }}>Form pesan belum aktif. Kirim email ke <a href={`mailto:${info.data.email}`}><b>{info.data.email}</b></a> dari alamat akunmu, ya.</p>
          ) : (
            <p style={{ flex: 1 }}>Form pesan belum aktif di server ini.</p>
          )}
        </div>
      )}
      {info.data?.enabled && ticket !== null && (
        <div className="card bt-done" role="status">
          <h3>Pesanmu terkirim</h3>
          <p>Nomor tiket <b>#{ticket}</b>. Balasan tim IqraPath akan masuk ke <b>{email}</b>. Kami juga mengirim tanda terima ke email itu.</p>
          <button type="button" className="btn btn-ghost btn-block" onClick={() => setTicket(null)}>Kirim pesan lain</button>
        </div>
      )}
      {info.data?.enabled && ticket === null && (
        <form className="card bt-form" onSubmit={send}>
          <fieldset>
            <legend>Tentang apa?</legend>
            <div className="bt-cats">
              {(Object.keys(SUPPORT_CATEGORIES) as SupportCategory[]).map((k) => (
                <button key={k} type="button" className="chip-btn" aria-pressed={category === k} onClick={() => setCategory(k)}>{SUPPORT_CATEGORIES[k]}</button>
              ))}
            </div>
          </fieldset>
          <label className="field">
            <span>Judul singkat</span>
            <input className="input" value={subject} maxLength={120} onChange={(e) => setSubject(e.target.value)} placeholder="Contoh: Audio ayat tidak bunyi" />
          </label>
          <label className="field">
            <span>Ceritakan kendalanya</span>
            <textarea className="input bt-msg" rows={6} value={message} maxLength={MAX} onChange={(e) => setMessage(e.target.value)} placeholder="Apa yang terjadi, di halaman mana, dan sejak kapan." />
          </label>
          {message.length > MAX - 300 && <p className="help" style={{ textAlign: 'right' }}>{message.length}/{MAX}</p>}
          <p className="help">Balasan dikirim ke email akunmu ({email}). Jangan menulis kode lisensi lengkap atau kata sandi.</p>
          <p className="error" role="alert">{error}</p>
          <button type="submit" className="btn btn-primary btn-block" disabled={!ready || sending}>
            {sending ? <><span className="spinner" aria-hidden="true" /> Mengirim…</> : 'Kirim pesan'}
          </button>
        </form>
      )}
    </div>
  )
}
